import 'hammerjs';
import { Component, computed, effect, inject, signal, ViewChild, OnInit } from '@angular/core';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { SeriesClickEvent } from '@progress/kendo-angular-charts';
import { ExcelModule, GridDataResult, GridModule } from '@progress/kendo-angular-grid';
import { BaseComponent } from '../../../core/shared/common/base.component';
import { AsyncPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { LoaderModule, SkeletonModule } from '@progress/kendo-angular-indicators';
import { LabelModule } from '@progress/kendo-angular-label';
import { PDFExportComponent, PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import {
  AnalyticsFilterComponent,
  FilterConfig,
  FilterState,
  TimeRange,
} from 'src/app/core/components/analytics-filter';
import { BaseAnalyticsPageComponent } from 'src/app/core/components/base-analytics-page';
import { AutomatedResponseUsageComponent } from '../../customer/analytics/components/soc-v2/components/automated-response-usage/automated-response-usage.component';
import { CustomerDispositionGridComponent } from '../../customer/analytics/components/soc-v2/components/customer-disposition-grid/customer-disposition-grid.component';
import { DispositionStatsComponent } from '../../customer/analytics/components/soc-v2/components/disposition-stats/disposition-stats.component';
import { EventsToEscalationsBarComponent } from '../../customer/analytics/components/soc-v2/components/events-to-escalations-bar/events-to-escalations-bar.component';
import { TicketCategoryPerformanceComponent } from '../../customer/analytics/components/soc-v2/components/ticket-category-performance/ticket-category-performance.component';
import { TicketClosureOutcomesComponent } from '../../customer/analytics/components/soc-v2/components/ticket-closure-outcomes/ticket-closure-outcomes.component';
import { TicketDispositionGridComponent } from '../../customer/analytics/components/soc-v2/components/ticket-disposition-grid/ticket-disposition-grid.component';
import { TopMttdDetectionsComponent } from '../../customer/analytics/components/soc-v2/components/top-mttd-detections/top-mttd-detections.component';
import { UnifiedGeoMapComponent } from '../../customer/analytics/components/soc-v2/components/unified-geo-map/unified-geo-map.component';
import { AdrManualComponent } from '../../customer/analytics/components/soc-v2/components/adr-manual/adr-manual.component';
import { SuspectAssetsComponent } from '../../customer/analytics/components/soc-v2/components/suspect-assets/suspect-assets.component';
import { SuspectUsersComponent } from '../../customer/analytics/components/soc-v2/components/suspect-users/suspect-users.component';
import { TopDetectionsComponent } from '../../customer/analytics/components/soc-v2/components/top-detections/top-detections.component';
import { EventsEscalationsDialogComponent } from '../../customer/analytics/components/soc-v2/components/events-escalations-dialog/events-escalations-dialog.component';
import { AlertDataDialogComponent } from 'src/app/core/components/alert-data-dialog';
import { TicketDataDialogComponent } from 'src/app/core/components/ticket-data-dialog';
import { AlertDataDialogConfig, TicketDataDialogConfig } from 'src/app/core/models/dialog-config';
import { checkIsDemoOrg } from 'src/app/core/models/organizations';
import {
  AnalyticsService,
  AlertStatSummaryResponse,
  SlaAdherenceResponse,
  ticketTrendsResponse,
  QueryType,
} from 'src/app/core/services/analytics.service';
import {
  SocAnalyticsParams,
  SocDataService,
} from '../../customer/analytics/components/soc-v2/services/soc-data.service';
import { SlaAdherenceDetailsService } from '../../customer/msp-portal/analytics/components/sla-adherence-card/services/slaadherencedetails.service';
import { ProductService } from '../../customer/msp-portal/analytics/services/product.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';
import { infoCircleIcon, moreVerticalIcon, sparklesIcon, filterIcon, exportIcon } from '@progress/kendo-svg-icons';
import { forkJoin, finalize, Observable } from 'rxjs';
import {
  EventsVsEscalationsChartData,
  MttdStatData,
  MttrStatData,
  TopMttdDetectionsData,
} from 'src/app/core/models/analytics/soc-models';
import { IndustryResult, Organization, Product } from 'src/app/core/models/entities';
import { DistillationFunnelComponent } from '../../customer/msp-portal/analytics/components/distillation-funnel/distillation-funnel.component';
import { GeneralOrganizationService } from '../../admin/organizations/components/general/service/general-organization.service';
import { OrganizationService } from '../../admin/organizations/services/organization.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    DropDownListModule,
    DateInputsModule,
    PDFExportModule,
    LabelModule,
    LayoutModule,
    SuspectUsersComponent,
    SuspectAssetsComponent,
    UnifiedGeoMapComponent,
    RouterModule,
    TopDetectionsComponent,
    AdrManualComponent,
    IconsModule,
    TooltipsModule,
    DistillationFunnelComponent,
    EventsToEscalationsBarComponent,
    DispositionStatsComponent,
    TopMttdDetectionsComponent,
    TicketDispositionGridComponent,
    TicketCategoryPerformanceComponent,
    AutomatedResponseUsageComponent,
    TicketClosureOutcomesComponent,
    BaseAnalyticsPageComponent,
    AnalyticsFilterComponent,
    LoaderModule,
    SkeletonModule,
    DialogsModule,
    GridModule,
    ExcelModule,
    AsyncPipe,
    CustomerDispositionGridComponent,
    EventsEscalationsDialogComponent,
    AlertDataDialogComponent,
    TicketDataDialogComponent,
  ],
  templateUrl: './dashboard.page.html',
  styleUrl: './dashboard.page.scss',
  providers: [
    AnalyticsService,
    SocDataService,
    ProductService,
    SlaAdherenceDetailsService,
    GeneralOrganizationService,
    OrganizationService,
  ],
})
export class DashboardPage extends BaseComponent implements OnInit {
  @ViewChild('socPdf') socPdf!: PDFExportComponent;

  public productService = inject(ProductService);
  public analytics = inject(AnalyticsService);
  private socDataService = inject(SocDataService);
  public generalOrgService = inject(GeneralOrganizationService);
  public orgService = inject(OrganizationService);
  public secondaryOrgService = new OrganizationService();

  // SLA details dialog functionality
  public readonly slaGridService = inject(SlaAdherenceDetailsService);
  public showSlaPopup = signal(false);

  // Icons
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public sparklesIcon: SVGIcon = sparklesIcon;
  public filterIcon: SVGIcon = filterIcon;
  public exportIcon: SVGIcon = exportIcon;

  // Expose QueryType enum for template use
  public QueryType = QueryType;

  // Filter signals and config
  public selectedTimeRange = signal<TimeRange>({ text: 'Last 30 days', value: '30d' });
  public startDate = signal<Date | null>(new Date(new Date().setDate(new Date().getDate() - 30)));
  public endDate = signal<Date | null>(new Date());
  public selectedTools = signal<any[]>([]);
  selectedServiceGroups = signal<string[]>([]);
  products = signal<Product[]>([]);
  industries = signal<IndustryResult[]>([]);
  selectedIndustries = signal<IndustryResult[]>([]);
  selectedOrganizations = signal<Organization[]>([]);
  organizations = signal<Organization[]>([]);
  private toolEnabledOrgs = signal<number[]>([]);

  public filterConfig: FilterConfig = {
    showTimeRange: true,
    showCustomerFilters: true,
    showServiceFilters: true,
    showToolFilters: true,
    showCustomFilters: false,
    showApplyButton: true,
    showResetButton: true,
  };
  // Formatted date range with proper timezone for child components
  public dateRangeArray: string[] = ['2023-03-01T00:00:00.000Z', '2023-03-31T23:59:59.999Z'];
  // Dates - UPDATED: startDate and endDate are now signals defined above
  public startDateString: string = '';
  public endDateString: string = '';

  public organizationId: number | null = null;

  public eventsVsEscalationsChartData = signal<EventsVsEscalationsChartData>({
    chart_data: [],
    current_reduction_percentage: 0,
    previous_reduction_percentage: 0,
    reduction_percentage_change: 0,
  });
  public destinationMapData = signal<any>(null);
  public sourceMapData = signal<any>(null);
  public topSuspectUsersData = signal<any>(null);
  public topUserDetailData = signal<any>([]);
  public topSuspectAssetsData = signal<any>(null);
  public topAssetDetailData = signal<any>(null);
  public topDetectionsData = signal<any>(null);
  public adrManualData = signal<any>(null);
  public mttdLineGraphData = signal<any>(null);
  public mttrLineGraphData = signal<any>(null);
  public topMttdDetectionsData = signal<TopMttdDetectionsData[]>([]);
  public AlertStatSummary = signal(new AlertStatSummaryResponse());
  public ticketTrends = signal(new ticketTrendsResponse());
  public slaAdherence = signal(new SlaAdherenceResponse());
  public averageMttd = 0;
  public averageMttr = 0;
  public mttdStatData = signal<MttdStatData>(new MttdStatData());
  public mttrStatData = signal<MttrStatData>(new MttrStatData());
  public ticketsDispositionData = signal<any[]>([]);
  public ticketCategoryPerformanceData = signal<any[]>([]);
  public automatedResponseUsageData = signal<any[]>([]);
  public customerDispositionData = signal<any[]>([]);

  // Loading state for drilldown operations
  public topUserDetailLoading = signal<boolean>(false);
  public topAssetDetailLoading = signal<boolean>(false);

  // Alert Data Dialog state
  public alertDialogConfig = signal<AlertDataDialogConfig | null>(null);
  public showAlertDialog = signal(false);

  // Ticket Data Dialog state
  public ticketDialogConfig = signal<TicketDataDialogConfig | null>(null);
  public showTicketDialog = signal(false);

  // Events vs Escalations -> DataTable dialog state
  public showEventsEscalationsDialog = signal(false);

  // Define visible columns for SOC v2 distillation funnel dialogs
  public socAlertVisibleColumns: string[] = [
    'event_name',
    'customer',
    'case_id',
    'source_ip',
    'destination_ip',
    'case_closed_on',
    'case_severity',
    'zoho_ticket_number',
    'closure_reason',
    'mitre_tactic',
    'event_occurred_on',
    'alert_received_on',
    'case_created_on',
    'case_tt_closed_min',
    'triage_type',
    'case_source',
    'close_reason_justification',
    'tracking_id',
    'sla_exceeded',
    'tags',
  ];

  public socTicketVisibleColumns: string[] = [
    'customer',
    'request_id',
    'created_time',
    'subject',
    'priority',
    'ticket_source',
    'ticket_status',
    'customer_defined_status',
    'sla_violation_type',
    'ticket_closed_time',
    'mean_time_to_resolve',
  ];

  // Computed properties for QueryType logic
  hasExplicitSelection = computed(() => {
    const orgsSelected = this.selectedOrganizations().length > 0;
    const toolsSelected = this.selectedTools().length > 0;
    return orgsSelected || toolsSelected;
  });

  queryType = computed(() => {
    return this.hasExplicitSelection() ? QueryType.Filtered : QueryType.All;
  });

  // Computed properties for service and organization filtering
  groupedServices = computed(() => {
    const uniqueGroups = new Set<string>();
    return this.products().filter((product) => {
      if (!uniqueGroups.has(product.group)) {
        uniqueGroups.add(product.group);
        return true;
      }
      return false;
    });
  });

  filteredProductsByService = computed(() => {
    const selectedGroups = this.selectedServiceGroups();

    if (selectedGroups.length === 0) {
      return this.products();
    }

    return this.products().filter((product) => selectedGroups.includes(product.group));
  });

  // Computed property for filtered org IDs - automatically updates when dependencies change
  public filteredOrgIds = computed(() => {
    const selectedOrgIds = this.getSelectedOrgIds();
    const selectedTools = this.selectedTools();
    const toolOrgs = this.toolEnabledOrgs();

    // No tools selected - use normal customer filtering
    if (selectedTools.length === 0) {
      const fallbackIds = this.getFallbackOrgIds(selectedOrgIds);
      return fallbackIds;
    }

    // Tools selected - find intersection
    if (selectedOrgIds.length > 0) {
      const intersection = selectedOrgIds.filter((id) => toolOrgs.includes(id));

      // Fallback if no intersection
      if (intersection.length === 0) {
        console.warn('REACTIVE: No selected customers have selected tools, using parent org');
        return this.organizationId ? [this.organizationId] : [];
      }

      return intersection;
    }

    // No customers selected, use all tool-enabled orgs
    const result = toolOrgs.length > 0 ? toolOrgs : this.organizationId ? [this.organizationId] : [];
    return result;
  });

  constructor() {
    super();

    // Initialize the main orgService state to prevent undefined errors
    this.orgService.state = {
      skip: 0,
      take: 200,
      sort: [{ field: 'name', dir: 'asc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };

    this.secondaryOrgService.state = {
      skip: 0,
      take: 200,
      sort: [{ field: 'name', dir: 'asc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    };

    // Effect 1: Service group changes
    effect(
      () => {
        const selectedServiceGroups = this.selectedServiceGroups();
        this.onServiceChange(selectedServiceGroups);
      },
      { allowSignalWrites: true }
    );

    // Effect 2: Industry changes - affects organization filtering
    effect(
      () => {
        const selectedIndustries = this.selectedIndustries();

        if (!this.orgService.state.filter) {
          this.orgService.state.filter = { logic: 'and', filters: [] };
        }

        // Drop any previous industry filter (flat eq or the multi-industry OR group)
        this.orgService.state.filter.filters = this.orgService.state.filter.filters.filter((f: any) => {
          if (f.field === 'industry') return false;
          if (Array.isArray(f.filters)) return !f.filters.some((sub: any) => sub.field === 'industry');
          return true;
        });

        if (selectedIndustries.length === 0) {
          this.selectedOrganizations.set([]);
          this.orgService.read();
          return;
        }

        if (selectedIndustries.length === 1) {
          this.orgService.state.filter.filters.push({
            field: 'industry',
            operator: 'eq',
            value: selectedIndustries[0].id,
          });
        } else {
          this.orgService.state.filter.filters.push({
            logic: 'or',
            filters: selectedIndustries.map((industry) => ({
              field: 'industry',
              operator: 'eq',
              value: industry.id,
            })),
          });
        }

        // industries with more orgs than that get truncated here - raise it if that's hit.
        this.orgService.read((result) => {
          this.selectedOrganizations.set((result.data as Organization[]) || []);
        });
      },
      { allowSignalWrites: true }
    );

    // Effect 3: Tool changes - loads organizations with selected tools
    effect(
      () => {
        const tools = this.selectedTools();

        if (tools.length === 0) {
          this.toolEnabledOrgs.set([]);
          return;
        }

        console.log('%c REACTIVE - Tools Changed', 'background: #cc6600; color: white', { tools });

        if (!this.secondaryOrgService.state.filter) {
          this.secondaryOrgService.state.filter = { logic: 'and', filters: [] };
        }

        const filterParts = tools.map((tool) => `products eq ${tool.id || tool}`);
        const filterString = filterParts.join(' or ');
        this.secondaryOrgService.customFilter = filterString;
        this.secondaryOrgService.appendQueryString = `parent_id=${this.organizationId}`;

        this.secondaryOrgService.read((data) => {
          const orgIds = data.data.map((org) => org.id);
          console.log('%c REACTIVE - Tool Orgs Loaded', 'background: #009900; color: white', { orgIds, tools });
          this.toolEnabledOrgs.set(orgIds);
        });
      },
      { allowSignalWrites: true }
    );

    // Effect 4: Debug logging for organization changes
    effect(() => {
      const selectedOrgs = this.selectedOrganizations();
      console.log('%c PARENT - Selected organizations changed', 'background: #00aa00; color: white', {
        selectedOrgs,
        count: selectedOrgs.length,
        organizationId: this.organizationId,
      });
    });

    // Initialize date string properties with current signal values
    this.updateDateStrings();
  }

  ngOnInit(): void {
    // Set organization ID and initialize data - following partner analytics pattern
    this.organizationId = 1;

    // Load initial data
    this.loadProducts();
    this.loadindustryItems();

    // Initial API call after setup
    this.applyFilters();
  }

  private loadindustryItems() {
    this.generalOrgService
      .getIndustries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (industries: any) => {
          this.industries.set(industries.results as IndustryResult[]);
        },
        error: (error: any) => {
          console.error('Error fetching industries:', error);
        },
      });
  }

  onSuspectUsersSeriesClick($event: SeriesClickEvent) {
    const seriesData = $event.sender.configurationService.store.series[0].data[0];
    const topSuspectUsersData = this.topSuspectUsersData()[0];
    if (seriesData.description !== topSuspectUsersData.description) {
      return;
    }

    // Use the actual date signals instead of the empty selectedDateRange
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) {
      console.warn('Start date or end date is not set');
      return;
    }

    const params: SocAnalyticsParams = {
      alert_received_on__range: `${startDate.toISOString()},${endDate.toISOString()}`,
      username__icontains: $event.category,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    this.socDataService
      .fetchTopUserDetail(this.filteredOrgIds()[0], params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.topUserDetailData.set(response);
        },
        error: (error) => {
          console.error('Error fetching Top User Detail:', error);
        },
      });
  }

  onTopDetecttionsCaseEscalatedCheckboxChanged($event: boolean) {
    // Use the actual date signals instead of the empty selectedDateRange
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) {
      console.warn('Start date or end date is not set');
      return;
    }

    const params: SocAnalyticsParams = {
      alert_received_on__range: `${startDate.toISOString()},${endDate.toISOString()}`,
      is_escalated: $event ? $event : null,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    this.socDataService
      .fetchTopDetectionsData(this.filteredOrgIds(), params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response) {
            this.topDetectionsData.set(response);
          }
        },
        error: (error) => {
          console.error('Error fetching top detections data with escalation filter:', error);
        },
      });
  }

  onSuspectAssetsSeriesClick($event: SeriesClickEvent) {
    const seriesData = $event.sender.configurationService.store.series[0].data[0];
    const topSuspectAssetsData = this.topSuspectAssetsData()[0];

    if (seriesData.description !== topSuspectAssetsData.description) {
      return;
    }

    // Use the actual date signals instead of the empty selectedDateRange
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) {
      console.warn('Start date or end date is not set');
      return;
    }

    const params: SocAnalyticsParams = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      source_ip: $event.category,
    };

    this.socDataService
      .fetchTopAssetDetailData(this.filteredOrgIds()[0], params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.topAssetDetailData.set(response);
        },
        error: (error) => {
          console.error('Error fetching Top Asset Detail:', error);
        },
      });
  }

  // Progressive loading state signals - loads data in phases from top to bottom
  public isCriticalDataLoading = signal(false); // Phase 1: Distillation funnel + Events vs Escalations
  public isPhase2Loading = signal(false); // Phase 2: Disposition Stats + Top MTTD + Disposition Grids
  public isPhase3Loading = signal(false); // Phase 3: Automated Response + Ticket Closure + Category Performance
  public isPhase4Loading = signal(false); // Phase 4: Top Detections + Suspect Users/Assets + Geo Distribution

  applyFilters() {
    console.log('%c DASHBOARD - applyFilters() called', 'background: #0066ff; color: white');
    const value = this.selectedTimeRange().value;

    if (value === '7d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 7)));
      this.endDate.set(new Date());
    } else if (value === '30d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 30)));
      this.endDate.set(new Date());
    } else if (value === '90d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 90)));
      this.endDate.set(new Date());
    }

    if (!this.organizationId || !this.startDate() || !this.endDate()) {
      console.warn('Missing required data for analytics:', {
        organizationId: this.organizationId,
        startDate: this.startDate(),
        endDate: this.endDate(),
      });
      return;
    }

    // Update date strings - normalize to day boundaries in UTC
    const startDate = new Date(this.startDate()!);
    startDate.setUTCHours(0, 0, 0, 0);
    this.startDateString = startDate.toISOString();

    const endDate = new Date(this.endDate()!);
    endDate.setUTCHours(23, 59, 59, 999);
    this.endDateString = endDate.toISOString();

    // Update dateRangeArray for child components (without overwriting normalized date strings)
    this.dateRangeArray = [this.startDateString, this.endDateString];

    // Create base parameters for SOC analytics
    const dateRange = `${this.startDateString},${this.endDateString}`;
    const params: SocAnalyticsParams = {
      alert_received_on__range: dateRange,
      created_time__range: dateRange,
      startDate: this.startDateString,
      endDate: this.endDateString,
      type: this.queryType(),
    };

    // Add security_tool parameter if tools are selected
    const securityTool = this.getSecurityToolsParameter();
    if (securityTool) {
      (params as any).security_tool = securityTool;
    }

    // Reset all loading states for progressive loading
    this.isCriticalDataLoading.set(true);
    this.isPhase2Loading.set(true);
    this.isPhase3Loading.set(true);
    this.isPhase4Loading.set(true);

    // Phase 1: Critical data - distillation funnel and events vs escalations
    // Use filteredOrgIds() for multi-tenant support - returns [1] on initial load, filtered IDs when org selected
    const orgIds = this.filteredOrgIds();
    forkJoin({
      eventsVsEscalations: this.socDataService.fetchEventsVsEscalationsChartData(orgIds, params),
      alertStatSummary: this.socDataService.fetchAlertStatSummary(
        orgIds,
        this.queryType(),
        params.startDate,
        params.endDate,
        params.security_tool
      ),
      ticketTrends: this.socDataService.fetchTicketTrends(
        orgIds,
        this.queryType(),
        params.startDate,
        params.endDate,
        params.security_tool
      ),
      slaAdherenceData: this.socDataService.fetchSlaVoilationsData(orgIds, {
        startDate: params.startDate,
        endDate: params.endDate,
        security_tool: params.security_tool,
      }),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isCriticalDataLoading.set(false);
          // Start Phase 2 loading after Phase 1 completes
          this.loadPhase2Data(params);
        })
      )
      .subscribe({
        next: (response) => {
          // Set critical data - eventsVsEscalations and distillation funnel data
          this.eventsVsEscalationsChartData.set(response.eventsVsEscalations);
          this.AlertStatSummary.set(response.alertStatSummary || new AlertStatSummaryResponse());
          this.ticketTrends.set(response.ticketTrends || new ticketTrendsResponse());
          this.slaAdherence.set(response.slaAdherenceData || new SlaAdherenceResponse());
        },
        error: (error) => {
          console.error('Error loading critical SOC analytics:', error);
        },
      });
  }

  /**
   * Phase 2: Disposition Stats + Top MTTD Detections + Disposition Grids
   * Loads key metrics and disposition data
   */
  private loadPhase2Data(params: SocAnalyticsParams): void {
    this.isPhase2Loading.set(true);

    // Use filteredOrgIds() for multi-tenant support
    const orgIds = this.filteredOrgIds();

    forkJoin({
      mttdLineGraphData: this.socDataService.fetchMttdLineGraphData(orgIds, params),
      mttrLineGraphData: this.socDataService.fetchMttrLineGraphData(orgIds, params),
      mttdStatData: this.socDataService.fetchMttdStatData(orgIds, params),
      mttrStatData: this.socDataService.fetchMttrStatData(orgIds, params),
      topMttdDetectionsData: this.socDataService.fetchTopMttdDetectionsData(orgIds, params),
      ticketsDispositionData: this.socDataService.fetchTicketsDispositionData(orgIds, params),
      customerDispositionData: this.socDataService.fetchCustomerDispositionData(orgIds, params),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isPhase2Loading.set(false);
          // Start Phase 3 after Phase 2 completes
          this.loadPhase3Data(params);
        })
      )
      .subscribe({
        next: (response) => {
          this.mttdLineGraphData.set(response.mttdLineGraphData);
          this.mttrLineGraphData.set(response.mttrLineGraphData);
          this.mttdStatData.set(response.mttdStatData);
          this.mttrStatData.set(response.mttrStatData);
          this.averageMttd = response.mttdStatData?.current_average_mttd || 0;
          this.averageMttr = response.mttrStatData?.current_average_mttr_hours || 0;
          this.topMttdDetectionsData.set(response.topMttdDetectionsData || []);
          this.ticketsDispositionData.set(response.ticketsDispositionData || []);
          this.customerDispositionData.set(response.customerDispositionData || []);
        },
        error: (error) => {
          console.error('Error loading Phase 2 data:', error);
          // Continue to Phase 3 even if Phase 2 fails
          this.loadPhase3Data(params);
        },
      });
  }

  /**
   * Phase 3: Automated Response + Ticket Closure + Category Performance + ADR
   * Loads ticket-related analytics
   */
  private loadPhase3Data(params: SocAnalyticsParams): void {
    this.isPhase3Loading.set(true);

    // Use filteredOrgIds() for multi-tenant support
    const orgIds = this.filteredOrgIds();

    forkJoin({
      automatedResponseUsageData: this.socDataService.fetchAutomatedResponseUsageData(orgIds, params),
      ticketCategoryPerformanceData: this.socDataService.fetchTicketCategoryPerformanceData(orgIds, params),
      adrManualData: this.socDataService.fetchAdrManualData(orgIds, params),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isPhase3Loading.set(false);
          // Start Phase 4 after Phase 3 completes
          this.loadPhase4Data(params);
        })
      )
      .subscribe({
        next: (response) => {
          this.automatedResponseUsageData.set(response.automatedResponseUsageData || []);
          this.ticketCategoryPerformanceData.set(response.ticketCategoryPerformanceData || []);
          this.adrManualData.set(response.adrManualData);
        },
        error: (error) => {
          console.error('Error loading Phase 3 data:', error);
          // Continue to Phase 4 even if Phase 3 fails
          this.loadPhase4Data(params);
        },
      });
  }

  /**
   * Phase 4: Top Detections + Suspect Users/Assets + Geographic Distribution
   * Loads detailed drill-down data
   */
  private loadPhase4Data(params: SocAnalyticsParams): void {
    this.isPhase4Loading.set(true);

    // Use filteredOrgIds() for multi-tenant support
    const orgIds = this.filteredOrgIds();

    forkJoin({
      topDetectionsData: this.socDataService.fetchTopDetectionsData(orgIds, params),
      topSuspectUsers: this.socDataService.fetchTopSuspectUsers(orgIds, params),
      topSuspectAssets: this.socDataService.fetchTopSuspectAssets(orgIds, params),
      topDetections: this.socDataService.fetchTopDetections(orgIds, params),
      sourceMap: this.socDataService.fetchSourceMap(orgIds, params),
      destinationMap: this.socDataService.fetchDestinationMap(orgIds, params),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isPhase4Loading.set(false);
        })
      )
      .subscribe({
        next: (response) => {
          this.topDetectionsData.set(response.topDetectionsData);
          this.topSuspectUsersData.set(response.topSuspectUsers);
          this.topSuspectAssetsData.set(response.topSuspectAssets);
          this.topUserDetailData.set(response.topDetections);
          this.topAssetDetailData.set([]);
          this.sourceMapData.set(response.sourceMap);
          this.destinationMapData.set(response.destinationMap);
        },
        error: (error) => {
          console.error('Error loading Phase 4 data:', error);
        },
      });
  }

  // NEW: Event handlers for the reusable analytics filter component
  public onTimeRangeChange(selectedRange: TimeRange): void {
    this.selectedTimeRange.set(selectedRange);
    // Update string properties when time range changes
    this.updateDateStrings();
    // Removed applyFilters() call - it will only be called by the button now
  }

  // Helper method to update date string properties - following partner analytics pattern
  private updateDateStrings(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (startDate && endDate) {
      // No hour modification - use dates as-is (matching partner analytics)
      this.startDateString = startDate.toISOString();
      this.endDateString = endDate.toISOString();

      // Update dateRangeArray for child components
      this.dateRangeArray = [this.startDateString, this.endDateString];
    } else {
      this.startDateString = '';
      this.endDateString = '';
      this.dateRangeArray = [];
    }
  }

  // Update startDate when the date picker value changes
  public onStartDateChange(newDate: Date | null): void {
    if (newDate) {
      // No hour modification - matching partner analytics pattern
      this.startDate.set(newDate);
      this.startDateString = newDate.toISOString();
    } else {
      this.startDate.set(null);
      this.startDateString = '';
    }
    // No API call here
  }

  // Update endDate when the date picker value changes
  public onEndDateChange(newDate: Date | null): void {
    if (newDate) {
      // No hour modification - matching partner analytics pattern
      this.endDate.set(newDate);
      this.endDateString = newDate.toISOString();
    } else {
      this.endDate.set(null);
      this.endDateString = '';
    }
    // No API call here
  }

  public onApplyFilters(_filterState: FilterState): void {
    // Apply filters and refresh data
    this.applyFilters();
  }

  private loadProducts(): void {
    if (!this.organizationId) {
      return;
    }

    (this.productService as any).setOrganizationId(this.organizationId);
    (this.productService as any).read((response: any) => {
      console.log('Products loaded for SOC Security Tools filter:', response.data);
      this.products.set(response.data || []);
    });
  }

  public onToolsChange(tools: any[]): void {
    this.selectedTools.set(tools);
  }

  /**
   * Convert selected tools to security_tool parameter format for backend API calls
   */
  public getSecurityToolsParameter(): string | undefined {
    const tools = this.selectedTools();
    if (tools.length === 0) {
      return undefined;
    }

    // Return product IDs format: 2,3
    return tools.map((tool: any) => tool.id || tool).join(',');
  }

  // Helper methods for organization filtering
  private getSelectedOrgIds(): number[] {
    const selectedOrgs = this.selectedOrganizations();
    return selectedOrgs.length > 0 ? (selectedOrgs.map((org) => org.id).filter((id) => id !== null) as number[]) : [];
  }

  private getFallbackOrgIds(selectedOrgIds: number[]): number[] {
    return selectedOrgIds.length > 0 ? selectedOrgIds : this.organizationId ? [this.organizationId] : [];
  }

  // Additional event handlers for the reusable analytics filter component
  public onIndustryChange(industries: any[]): void {
    this.selectedIndustries.set(industries || []);
  }

  public onOrganizationsChange(organizations: any[]): void {
    this.selectedOrganizations.set(organizations);
  }

  public onServiceGroupChange(serviceGroups: string[]): void {
    this.selectedServiceGroups.set(serviceGroups);
  }

  public onResetFilters(): void {
    // Reset all filters to default values
    this.selectedTimeRange.set({ text: 'Last 30 days', value: '30d' });
    this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 30)));
    this.endDate.set(new Date());
    this.selectedIndustries.set([]);
    this.selectedOrganizations.set([]);
    this.selectedServiceGroups.set([]);
    this.selectedTools.set([]);

    // Apply the reset filters
    this.applyFilters();
  }

  // Service change handler for Effect 1
  public onServiceChange(selectedGroups: string[]): void {
    // Clear selected tools when service group selection changes
    this.selectedTools.set([]);
  }

  // SLA Adherence Dialog Methods - Following partner analytics pattern
  public onSlaCardClick(): void {
    if (!this.organizationId || !this.startDateString || !this.endDateString) {
      return; // Don't allow click if no org ID or dates
    }

    // Configure the SLA grid service with the correct URL and parameters
    const orgIds = this.filteredOrgIds();
    const currentQueryType = this.queryType();

    this.slaGridService.url = `${environment.apiUrl}/v1/analytics/administrative/slaadherencemetricsdetails(${this.organizationId})`;
    this.slaGridService.appendQueryString = `org_ids=${orgIds}&type=${currentQueryType}&current_start=${this.startDateString}&current_end=${this.endDateString}`;
    this.slaGridService.read();
    this.showSlaPopup.set(true);
  }

  public closeSlaPopup(): void {
    this.showSlaPopup.set(false);
  }

  /**
   * Opens the Events vs Escalations drill-down dialog
   */
  public onEventsToEscalationsChartClick(): void {
    // Ensure we have dates; fall back to signals if strings not set yet
    let start = this.startDateString;
    let end = this.endDateString;
    if ((!start || !end) && this.startDate() && this.endDate()) {
      const s = new Date(this.startDate()!);
      s.setHours(0, 0, 0, 0);
      start = s.toISOString();
      const e = new Date(this.endDate()!);
      e.setHours(23, 59, 59, 999);
      end = e.toISOString();
      this.startDateString = start;
      this.endDateString = end;
    }

    if (!this.organizationId || !start || !end) {
      console.warn('Cannot open Events vs Escalations dialog: missing required data');
      return;
    }

    this.showEventsEscalationsDialog.set(true);
  }

  /**
   * Closes the Events vs Escalations dialog
   */
  public onEventsEscalationsDialogClose(): void {
    this.showEventsEscalationsDialog.set(false);
  }

  /**
   * Opens alert data dialog for MTTD drill-down
   */
  public onOpenAlertDialogMTTD(): void {
    console.log('[DEBUG] onOpenAlertDialogMTTD triggered');
    const demostr = checkIsDemoOrg() ? '/demo' : '';
    const config: AlertDataDialogConfig = {
      organizationId: this.organizationId,
      startDate: this.startDateString,
      endDate: this.endDateString,
      queryType: this.queryType(),
      selectedOrgIds: this.filteredOrgIds(),
      event: 'raw_events',
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/alerteventsdetails/`,
      visibleColumns: this.socAlertVisibleColumns,
      securityTool: this.getSecurityToolsParameter(),
    };

    console.log('[DEBUG] MTTD config:', config);
    console.log('[DEBUG] startDateString:', this.startDateString, 'endDateString:', this.endDateString);
    this.alertDialogConfig.set(config);
    this.showAlertDialog.set(true);
    console.log('[DEBUG] showAlertDialog set to:', this.showAlertDialog());
  }

  /**
   * Opens alert data dialog for SLA violations drill-down
   */
  public onOpenAlertDialogSLA(): void {
    console.log('[DEBUG] onOpenAlertDialogSLA triggered');
    const demostr = checkIsDemoOrg() ? '/demo' : '';
    const config: AlertDataDialogConfig = {
      organizationId: this.organizationId,
      startDate: this.startDateString,
      endDate: this.endDateString,
      queryType: this.queryType(),
      selectedOrgIds: this.filteredOrgIds(),
      event: 'raw_events',
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/alerteventsdetails/`,
      visibleColumns: this.socAlertVisibleColumns,
      securityTool: this.getSecurityToolsParameter(),
      additionalParams: { $filter: "sla_exceeded eq 'Yes'" },
    };

    this.alertDialogConfig.set(config);
    this.showAlertDialog.set(true);
  }

  /**
   * Opens ticket data dialog for MTTR drill-down
   */
  public onOpenTicketDialogMTTR(): void {
    console.log('[DEBUG] onOpenTicketDialogMTTR triggered');
    const demostr = checkIsDemoOrg() ? '/demo' : '';
    const config: TicketDataDialogConfig = {
      organizationId: this.organizationId,
      startDate: this.startDateString,
      endDate: this.endDateString,
      queryType: this.queryType(),
      selectedOrgIds: this.filteredOrgIds(),
      event: 'escalations',
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/ticketeventsdetails/`,
      useDifferentDateField: true,
      visibleColumns: this.socTicketVisibleColumns,
      securityTool: this.getSecurityToolsParameter(),
      additionalParams: {
        $filter:
          "department_name eq 'SOC Incidents' and " +
          "(customer_defined_status eq 'Completed' or customer_defined_status eq 'Cancelled') and " +
          "ticket_closed_time ne null",
      },
    };

    this.ticketDialogConfig.set(config);
    this.showTicketDialog.set(true);
  }

  /**
   * Closes the alert data dialog
   */
  public onAlertDialogClose(): void {
    this.showAlertDialog.set(false);
    this.alertDialogConfig.set(null);
  }

  /**
   * Closes the ticket data dialog
   */
  public onTicketDialogClose(): void {
    this.showTicketDialog.set(false);
    this.ticketDialogConfig.set(null);
  }

  public allSlaData = (): Observable<GridDataResult> => {
    // Return SLA data from the partner analytics service call
    return new Observable((observer) => {
      observer.next({ data: [], total: 0 });
      observer.complete();
    });
  };

  // PDF Export method to be called by parent
  public onParentPdfExport(filename: string): void {
    console.log('SOC component received PDF export request with filename:', filename);
    if (this.socPdf) {
      this.socPdf.saveAs(filename);
    } else {
      console.error('SOC PDF export component not found');
    }
  }
}
